import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import useScrollToTop from '@/hooks/useScrollToTop';
import { ComicCard } from '@/pages/home/components/HomeSections';
import { allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const found = allComics.find((c) => c.slug === slug);
    if (found) {
      setComic(found);
      setNotFound(false);
      document.title = `${found.title} - 虚空韩漫网 | 韩漫在线阅读`;
    } else {
      setNotFound(true);
    }
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 text-sm mb-4">该漫画可能已被下架或链接有误</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const related = allComics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  const totalChapters = comic.chapters.length;

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema type="Article" data={{
        headline: `${comic.title} - 虚空韩漫网`,
        description: comic.description,
        author: comic.author,
        datePublished: comic.chapters[0]?.updateDate || comic.updateDate,
        dateModified: comic.updateDate,
        image: comic.coverImage,
      }} />
      <SEOSchema type="WebPage" data={{
        name: `${comic.title} - 虚空韩漫网`,
        description: `${comic.title}，作者${comic.author}。${comic.description.substring(0, 100)}`,
        url: `http://0661yes.cn/comic/${comic.slug}`,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: comic.category === 'love' ? '恋爱' : comic.category === 'action' ? '动作' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'thriller' ? '悬疑' : '全部', href: `/category/${comic.category}` },
              { label: comic.title },
            ]} />
          </div>

          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-14">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full lg:w-72">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 sticky top-24">
                  {!imgError ? (
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`虚空韩漫网 - ${comic.title}`}
                      className="w-full h-full object-cover object-top"
                      onError={() => setImgError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-background-200">
                      <span className="text-foreground-400 text-sm">{comic.title}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-foreground-500 text-sm">作者：<strong className="text-foreground-800">{comic.author}</strong></span>
                  <span className="text-foreground-300">|</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-500 text-xs flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400"></i>
                    </span>
                    <strong className="text-foreground-800">{comic.rating}</strong>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-500 text-xs flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    <strong className="text-foreground-800">{(comic.views / 10000).toFixed(0)}万</strong> 阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">{tag}</span>
                  ))}
                </div>

                <div className="mt-6 p-5 rounded-xl bg-background-100">
                  <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-2">作品简介</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-bold text-lg text-foreground-950">
                      章节列表
                      <span className="text-foreground-400 text-sm font-normal ml-2">共 {totalChapters} 话</span>
                    </h2>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-sort-asc mr-1"></i>正序
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto pr-1" id="chapter-list">
                    {comic.chapters.map((ch, index) => (
                      <a
                        key={ch.id}
                        href={`#chapter-${ch.id}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-accent-50 transition-colors cursor-pointer border border-transparent hover:border-accent-200/60"
                      >
                        <span className="text-sm text-foreground-800 font-medium truncate">{ch.title}</span>
                        <span className="text-foreground-400 text-[10px] whitespace-nowrap ml-2">{ch.updateDate}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {related.length > 0 && (
            <section className="bg-background-100 py-10 md:py-14">
              <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-product-shop="">
                  {related.map((r) => (
                    <ComicCard key={r.id} comic={r} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}